import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

export const DEFAULT_BROWSER_TAB_TITLE = 'OneHub';

/**
 * Replace favicon link tags in the document head.
 * Browsers (and our boot script in index.html) keep a persistent
 * <link rel="icon"> that react-helmet-async does not reliably update, so
 * org switches only took effect after a full refresh. Recreating the node
 * forces the tab icon to refresh.
 */
const setFaviconLink = (rel: string, href: string) => {
  const selector = rel === 'icon' ? 'link[rel="icon"]' : `link[rel~='${rel}']`;

  document.querySelectorAll(selector).forEach((el) => el.remove());

  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  document.head.appendChild(link);
};

export const DocumentHead = ({
  faviconLink,
  browserTabTitle,
}: {
  /**
   * `undefined` = org favicon not known yet — use localStorage cache.
   * `null` / `''` = known empty → restore defaults.
   * string = custom org favicon.
   */
  faviconLink?: string | null;
  browserTabTitle?: string;
}) => {
  useEffect(() => {
    // Settings not loaded yet — prefer the cached org favicon so Auth0Provider
    // mounting DocumentHead during loading doesn't flash the default icon.
    if (faviconLink === undefined) {
      const storedFavicon = window.localStorage.getItem('faviconLink');
      if (storedFavicon) {
        setFaviconLink('icon', storedFavicon);
        setFaviconLink('apple-touch-icon', storedFavicon);
      }
      return;
    }

    if (faviconLink) {
      window.localStorage.setItem('faviconLink', faviconLink);
    } else {
      window.localStorage.removeItem('faviconLink');
    }

    const iconHref = faviconLink || `${import.meta.env.BASE_URL}favicon.ico`;
    const appleIconHref = faviconLink || `${import.meta.env.BASE_URL}logo.png`;

    setFaviconLink('icon', iconHref);
    setFaviconLink('apple-touch-icon', appleIconHref);
  }, [faviconLink]);

  return (
    <Helmet>
      <title>{browserTabTitle || DEFAULT_BROWSER_TAB_TITLE}</title>
    </Helmet>
  );
};
